import { Alert, AlertDescription, AlertTitle, Button } from '@noor/ui';
import {
  sourceLabel,
  type ModerationOperation,
  type PendingSlotVersion,
  type Translate,
} from '@/entities/moderation';
import type { ModerationReview } from '../../model/use-moderation-review';

/**
 * Situational alerts every pending/decided view may need: the card failed
 * to load (data falls back to the request), the edit is missing from the
 * card, a delete is irreversible, and a sibling request waits on the same
 * entity. Each renders only when its condition holds.
 */
export function ReviewAlerts({ review, t }: { review: ModerationReview; t: Translate }) {
  const siblings = review.pendingSlots.filter(
    (slot) => slot.version.change_request?.id !== review.request.id,
  );
  // An approved delete removed the entity — its card is expected to be gone,
  // and the request's own baseline is the record, not a degraded fallback.
  const entityRemoved = review.operation === 'delete' && review.decision === 'approved';
  return (
    <>
      {review.cardStatus === 'error' && !entityRemoved && (
        <Alert variant="warning" data-testid="moderation-card-error">
          <AlertTitle>
            {t('review.cardError', 'Не удалось загрузить актуальную версию')}
          </AlertTitle>
          <AlertDescription>
            <p>
              {t(
                'review.cardErrorHint',
                'Показаны данные из самой заявки — они могут быть неполными.',
              )}
            </p>
            <Button
              type="button"
              size="sm"
              shape="rounded"
              variant="soft"
              onClick={review.refetchCard}
            >
              {t('table.retry', 'Повторить')}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {review.missingFromCard && (
        <Alert variant="warning" data-testid="moderation-missing-from-card">
          <AlertTitle>
            {t('review.missingTitle', 'Заявки нет в карточке модерации')}
          </AlertTitle>
          <AlertDescription>
            <p>
              {t(
                'review.missingHint',
                'Сравнение построено по данным заявки. Возможно, её уже рассмотрели или заменили более новой версией.',
              )}
            </p>
          </AlertDescription>
        </Alert>
      )}
      {review.decision === 'pending' && (
        <OperationAlert operation={review.operation} siblings={siblings} t={t} />
      )}
    </>
  );
}

function OperationAlert({
  operation,
  siblings,
  t,
}: {
  operation: ModerationOperation;
  siblings: PendingSlotVersion[];
  t: Translate;
}) {
  return (
    <>
      {operation === 'delete' && (
        <Alert variant="destructive" data-testid="moderation-delete-warning">
          <AlertTitle>
            {t('review.deleteTitle', 'Мерчант запрашивает удаление')}
          </AlertTitle>
          <AlertDescription>
            <p>
              {t(
                'review.deleteHint',
                'После одобрения объект будет удалён с витрины. Ниже — его текущая версия.',
              )}
            </p>
          </AlertDescription>
        </Alert>
      )}
      {operation !== 'update' && siblings.length > 0 && (
        <Alert variant="info" data-testid="moderation-sibling-note">
          <AlertTitle>
            {t('review.siblingTitle', 'По объекту есть ещё заявка')}
          </AlertTitle>
          <AlertDescription>
            <p>
              {t(
                'review.siblingHint',
                'Ожидает решения заявка из источника «{{source}}». Её можно рассмотреть отдельно.',
              ).replace(
                '{{source}}',
                siblings.map((sibling) => sourceLabel(sibling.slot, t)).join(', '),
              )}
            </p>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
